import {
  usePausePiiBackfill,
  useStartPiiBackfill,
} from "@/api/pii-vault/use-pii-backfill";
import type { PiiBackfillStateDto } from "@babackapp/private-api-client/generated/schema";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { subMonths } from "date-fns";
import { Loader2, Pause, Play } from "lucide-react";
import { useState } from "react";

const COLLECTION_COUNT = 3;
const DEFAULT_PARALLELISM = 10;
const MAX_PARALLELISM = 50;
const ALL_ORDERS = "all";

const ORDER_WINDOWS = [
  { value: "3", label: "Commandes : 3 derniers mois" },
  { value: "6", label: "Commandes : 6 derniers mois" },
  { value: "12", label: "Commandes : 12 derniers mois" },
  { value: ALL_ORDERS, label: "Commandes : toutes" },
];

type StartAction = "start" | "resume" | "widen" | "restart";

const START_LABELS: Record<StartAction, string> = {
  start: "Lancer",
  resume: "Reprendre",
  widen: "Continuer",
  restart: "Refaire",
};

/**
 * A finished shop is widened when the chosen window reaches older orders than
 * the run covered, and redone from scratch otherwise.
 */
export const startActionOf = (
  states: PiiBackfillStateDto[],
  orderMonths: number,
): StartAction => {
  if (states.length === 0) return "start";
  const isComplete =
    states.length === COLLECTION_COUNT && states.every((state) => state.isDone);
  if (!isComplete) return "resume";
  const coveredSince = states.find((s) => s.collection === "order")?.since;
  const requestedSince = orderMonths
    ? subMonths(new Date(), orderMonths).getTime()
    : null;
  const isWider =
    coveredSince != null &&
    (requestedSince === null || requestedSince < coveredSince);
  return isWider ? "widen" : "restart";
};

export function PiiBackfillControls({
  shopId,
  states,
}: {
  shopId: string;
  states: PiiBackfillStateDto[];
}) {
  const start = useStartPiiBackfill(shopId);
  const pause = usePausePiiBackfill(shopId);
  const [parallelism, setParallelism] = useState(DEFAULT_PARALLELISM);
  const [orderWindow, setOrderWindow] = useState("6");

  const orderMonths = orderWindow === ALL_ORDERS ? 0 : Number(orderWindow);
  const action = startActionOf(states, orderMonths);
  const isRunning = states.some((state) => !state.isDone && !state.isPaused);
  const isPending = start.isPending || pause.isPending;

  return (
    <div className="flex items-center gap-2 shrink-0">
      <Select value={orderWindow} onValueChange={setOrderWindow}>
        <SelectTrigger className="h-8 w-56">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ORDER_WINDOWS.map((window) => (
            <SelectItem key={window.value} value={window.value}>
              {window.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <label
        className="text-xs text-muted-foreground"
        title="Documents traités en même temps. Monte progressivement en surveillant Atlas et la latence de l'API."
      >
        Parallélisme
      </label>
      <Input
        type="number"
        min={1}
        max={MAX_PARALLELISM}
        value={parallelism}
        onChange={(event) => setParallelism(Number(event.target.value))}
        className="w-16 h-8"
      />
      <Button
        size="sm"
        disabled={isRunning || isPending}
        onClick={() =>
          start.mutate({
            params: { path: { shopId } },
            body: {
              parallelism,
              orderMonths,
              isRestart: action === "restart",
            },
          })
        }
      >
        {isPending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Play className="w-4 h-4" />
        )}
        {START_LABELS[action]}
      </Button>
      <Button
        size="sm"
        variant="secondary"
        disabled={!isRunning || isPending}
        onClick={() => pause.mutate({ params: { path: { shopId } } })}
      >
        <Pause className="w-4 h-4" />
        Pause
      </Button>
    </div>
  );
}
